/**
 * Marco de las pantallas sin sesión: foto del gimnasio en blanco y negro al fondo,
 * velada hasta el night del demo, con el grid, el noise y el glow radial teal→índigo
 * encima, y el panel vidriado centrado.
 *
 * La foto es decorativa (`alt=""`, `aria-hidden`): no aporta información y no se
 * anuncia. Va velada a propósito — el panel y los campos mandan, y el contraste del
 * texto no puede quedar a merced de qué zona de la imagen le toque atrás.
 *
 * Capas, de atrás hacia adelante: foto → velos (tinte + gradiente + viñeta) →
 * identidad (grid, noise, glow) → contenido. Todas las decorativas son
 * `pointer-events-none`; el contenido va `relative` para quedar por encima.
 */
import type { ReactNode } from "react";
import { VivaLogo } from "../../components/ui/VivaLogo.js";

export function AuthShell({
  title,
  kicker,
  description,
  children,
  footer,
}: {
  title: string;
  kicker: string;
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div className="theme-night relative isolate flex min-h-dvh flex-col items-center justify-center overflow-hidden bg-night px-4 py-10">
      {/* Foto. `sizes=100vw` + srcset: en un teléfono baja la de 1200px, no la de 2400. */}
      <picture>
        <source
          type="image/webp"
          srcSet="/img/login-bg-sm.webp 1200w, /img/login-bg.webp 2400w"
          sizes="100vw"
        />
        <img
          aria-hidden
          alt=""
          src="/img/login-bg.jpg"
          srcSet="/img/login-bg-sm.jpg 1200w, /img/login-bg.jpg 2400w"
          sizes="100vw"
          fetchPriority="high"
          className="pointer-events-none absolute inset-0 size-full object-cover object-[38%_center] select-none"
        />
      </picture>

      {/* Velos: la foto tiene zonas muy claras y no puede comerle contraste al panel. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-night/40" />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-night/75 via-night/20 to-night-deep/88"
      />
      {/* Viñeta con los mismos tokens `night` / `night-deep`, que acá adentro son oscuros. */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 bg-radial-[115%_85%_at_50%_50%] from-transparent from-0% via-night/30 via-55% to-night-deep/85"
      />

      {/* Identidad del demo, encima de la foto para que no se pierda. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-grid opacity-70" />
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-noise" />
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-glow" />

      <div className="relative flex w-full max-w-[420px] flex-col items-center gap-6">
        {/* La sombra despega el logo de la foto: `drop-shadow` sigue la silueta del
            SVG, que es lo que se ve — `text-shadow` no aplica a una imagen. */}
        <VivaLogo
          variant="lockup"
          className="h-11 w-auto [filter:drop-shadow(0_2px_16px_color-mix(in_srgb,var(--color-night-deep)_95%,transparent))]"
        />

        <div className="panel-glass w-full px-6 py-7">
          <p className="micro mb-3 text-accent">{kicker}</p>
          <h1 className="font-display text-[24px] leading-tight font-semibold tracking-tight">
            {title}
          </h1>
          {description ? (
            <p className="mt-2 text-[13px] leading-relaxed text-dim">{description}</p>
          ) : null}
          <div className="mt-6">{children}</div>
        </div>

        {footer ? (
          <div className="text-center text-[12px] text-dim [text-shadow:0_2px_14px_color-mix(in_srgb,var(--color-night-deep)_95%,transparent)]">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  );
}
